import { Link } from "react-router-dom";
import type { DocumentDto } from "../../api/types";
import { TypeChip } from "../TypeChip";
import { VisibilityBadge } from "../VisibilityBadge";
import { DocumentBody } from "./DocumentBody";
import { Comments } from "../comments/Comments";
import { accession, formatDate, formatYear, TYPE_LABELS } from "../../lib/format";
import { useCollections, collectionLabel } from "../../hooks/useCollections";

interface DocumentViewProps {
  doc: DocumentDto;
  backTo?: string;
  backLabel?: string;
}

/**
 * The full detail view shared by the canonical /documents/:contentId page and
 * the /blog/:slug route: a catalogue-style header, the type-specific body, the
 * item's tags, and its comment thread (keyed by content type + source id, so
 * both URLs for a journal entry show the same conversation).
 */
export function DocumentView({
  doc,
  backTo = "/search",
  backLabel = "← Back to search",
}: DocumentViewProps) {
  const { collections } = useCollections();
  const collectionName = collectionLabel(collections, doc.collection);

  return (
    <div className="page">
      <div className="container">
        <div className="doc">
          <p className="doc__back">
            <Link to={backTo}>{backLabel}</Link>
          </p>

          <div className="doc__head">
            <p className="eyebrow">
              <TypeChip type={doc.contentType} /> {TYPE_LABELS[doc.contentType]} ·{" "}
              <span className="accession">{accession(doc.contentType, doc.sourceId)}</span>
            </p>
            <h1 className="doc__title">{doc.title}</h1>
            <AccessNote doc={doc} />
            <div className="doc__meta">
              {doc.occurredAt && (
                <span>
                  {doc.contentType === "PHOTO"
                    ? formatYear(doc.occurredAt)
                    : formatDate(doc.occurredAt)}
                </span>
              )}
              {doc.source && <span>· {doc.source}</span>}
              <span>
                · in <Link to={`/collections/${doc.collection}`}>{collectionName}</Link>
              </span>
            </div>
          </div>

          <DocumentBody doc={doc} />

          {doc.tags.length > 0 && (
            <div className="doc__tags">
              {doc.tags.map((tag) => (
                <span key={tag} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <Comments contentType={doc.contentType} sourceId={doc.sourceId} />
        </div>
      </div>
    </div>
  );
}

/**
 * Says so when an item is not public. Only a viewer allowed to see it ever
 * gets here, so the wording is addressed to them; the admin also sees who a
 * shared item was shared with (the API sends that list to the admin only).
 */
function AccessNote({ doc }: { doc: DocumentDto }) {
  if (!doc.visibility || doc.visibility === "PUBLIC") return null;
  const text: Record<string, string> = {
    MEMBERS: "Only signed-in members can see this.",
    RESTRICTED:
      doc.sharedWith.length > 0
        ? `Shared with ${doc.sharedWith.join(", ")}.`
        : "Shared with you — not visible to the public.",
    PRIVATE: "Private — only you can see this.",
  };
  return (
    <p className="doc__access" role="note">
      <VisibilityBadge visibility={doc.visibility} /> {text[doc.visibility]}
    </p>
  );
}
